import { toBigInt } from '@/utils/bigNumber'
import { NormalizedUnitNumber } from '../types/NumericValues'
import { Address, formatUnits } from 'viem'
import { POINTS_LEADERBOARD_BLACKLIST, POINTS_LEADERBOARD_ITEMS } from '@/config/chain/points'
import { CheckedAddress } from '../types/CheckedAddress'
import { RankChange } from './types'

export const pointsFormatter = new Intl.NumberFormat('en-US', {
  notation: 'compact',
  maximumFractionDigits: 2,
})

export function formatPointsAmount({
  amount,
  pending,
  decimals = 18,
}: {
  amount: string
  pending?: string
  decimals?: number
}): {
  amount: NormalizedUnitNumber
  points: NormalizedUnitNumber
  formattedAmount: string
} {
  const amountBI = (toBigInt(amount) + toBigInt(pending ?? 0)) * 100n // multiply by 100
  const amountFormatted = formatUnits(amountBI, decimals)

  return {
    amount: NormalizedUnitNumber(amountFormatted),
    points: NormalizedUnitNumber(formatUnits(amountBI, decimals)),
    formattedAmount: pointsFormatter.format(Number(amountFormatted)),
  }
}

export function getRankFromData<T extends { recipient: string }>(
  leaderboard: T[] | null | undefined,
  account?: Address,
): number | undefined {
  if (!account) {
    return undefined
  }

  return leaderboard
    ?.filter((item) => !POINTS_LEADERBOARD_BLACKLIST.includes(item.recipient as CheckedAddress))
    .slice(0, POINTS_LEADERBOARD_ITEMS)
    .findIndex((item) => item.recipient === account)
}

export function getRankChange({
  currentRank,
  previousRank,
}: { currentRank?: number; previousRank?: number }): RankChange {
  if (typeof currentRank === 'undefined' && typeof previousRank === 'undefined') {
    return 'unavailable'
  }

  if (typeof currentRank === 'undefined' && typeof previousRank === 'number') {
    return 'decrease'
  }

  if (typeof currentRank === 'number' && typeof previousRank === 'undefined') {
    return 'increase'
  }

  if (typeof currentRank === 'number' && typeof previousRank === 'number') {
    if (currentRank < previousRank) {
      return 'increase'
    }

    if (currentRank > previousRank) {
      return 'decrease'
    }

    return 'no-change'
  }

  return 'unavailable'
}
